<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 animate-modalFade" v-if="!isClosed">
    <div class="w-full max-w-2xl bg-gray-800 text-gray-300 rounded-lg p-4 sm:p-6 flex flex-col gap-4 shadow-lg border border-gray-700 animate-scaleIn">
      <!-- Header with Countdown Timer and Close Button -->
      <div class="flex justify-between items-center">
        <div v-if="timeLeft !== null" class="font-semibold text-xxs sm:text-xs bg-gray-800 bg-opacity-90 border border-gray-600 rounded p-1">
          <div class="flex items-center space-x-2 sm:space-x-3 text-center">
            <span class="px-2 py-0.5 bg-gray-600 text-gray-100 rounded">
              {{ mapName || 'ไม่ระบุแผนที่' }}
            </span>
            <span :class="isDanger ? 'text-red-600' : 'text-yellow-700'">
              เหลือเวลา: {{ formatTime(timeLeft) }}
            </span>
          </div>
          <p v-if="!fightResult && isDanger" class="mt-1 text-xxs text-red-700 text-center">
            <i class="fas fa-exclamation-triangle mr-1"></i>
            คำเตือน: เหลือเวลาไม่ถึง 5 นาที!
          </p>
        </div>
        <button @click="closeModal" class="text-gray-300 hover:text-red-500 focus:outline-none" title="ปิดหน้าต่าง">
          <i class="fas fa-minus text-lg"></i>
        </button>
      </div>
      <!-- Ghost Section -->
      <div v-if="ghostData && !isLoading" class="flex flex-col items-center gap-3 bg-gray-900 border border-gray-700 rounded-md p-4">
        <div class="flex items-center gap-3 w-full">
          <img
            :src="ghostData?.imageUrl || 'default-ghost.png'"
            alt="Ghost"
            class="w-16 sm:w-20 h-16 sm:h-20 rounded-md object-cover border-2 border-red-600"
            @error="handleImageError"
          />
          <div class="flex-1">
            <h2 class="text-base sm:text-lg font-semibold text-red-600 text-center">
              {{ ghostData?.name || 'วิญญาณ' }}
            </h2>
            <div class="grid grid-cols-4 sm:grid-cols-7 gap-2 text-center mt-2 bg-gray-800 bg-opacity-30 p-2 rounded-md">
              <div class="flex flex-col items-center text-gray-100">
                <i class="fas fa-heartbeat text-red-600"></i>
                <p class="text-xs font-bold mt-1">{{ ghostStats.maxHp }}</p>
              </div>
              <div class="flex flex-col items-center text-gray-100">
                <i class="fas fa-fist-raised text-orange-600"></i>
                <p class="text-xs font-bold mt-1">{{ ghostStats.atk }}</p>
              </div>
              <div class="flex flex-col items-center text-gray-100">
                <i class="fas fa-running text-green-600"></i>
                <p class="text-xs font-bold mt-1">{{ ghostStats.evasion }}%</p>
              </div>
              <div class="flex flex-col items-center text-gray-100">
                <i class="fas fa-crosshairs text-yellow-600"></i>
                <p class="text-xs font-bold mt-1">{{ ghostStats.critical }}%</p>
              </div>
              <div class="flex flex-col items-center text-gray-100">
                <i class="fa-solid fa-shield-virus text-purple-600"></i>
                <p class="text-xs font-bold mt-1">{{ ghostStats.criticalReduction }}%</p>
              </div>
              <div class="flex flex-col items-center text-gray-100">
                <i class="fas fa-shield-alt text-blue-600"></i>
                <p class="text-xs font-bold mt-1">{{ ghostStats.defense }}</p>
              </div>
              <div class="flex flex-col items-center text-gray-100">
                <i class="fa-solid fa-person-rays text-indigo-600"></i>
                <p class="text-xs font-bold mt-1">{{ ghostStats.damageReduction }}%</p>
              </div>
            </div>
          </div>
        </div>
        <div class="w-full relative">
          <div class="bg-gray-500 h-5 rounded overflow-hidden">
            <div
              class="bg-red-600 h-5 rounded transition-all duration-200"
              :style="{ width: `${(currentGhostHp / ghostStats.maxHp * 100) || 0}%` }"
            ></div>
            <p class="absolute inset-0 flex items-center justify-center text-xs text-gray-100">
              HP: {{ currentGhostHp }} / {{ ghostStats.maxHp }}
            </p>
          </div>
        </div>
      </div>
      <!-- Player Section -->
      <div v-if="ghostData && !isLoading" class="flex flex-col items-center gap-3 bg-gray-900 border border-gray-700 rounded-md p-4">
        <div class="flex items-center gap-3 w-full">
          <div class="flex-1">
            <h2 class="text-base sm:text-lg font-semibold text-blue-600 text-center">{{ characterData?.characterName || 'ผู้เล่น' }}</h2>
            <div class="grid grid-cols-4 sm:grid-cols-7 gap-2 text-center mt-2 bg-gray-800 bg-opacity-30 p-2 rounded-md">
              <div class="flex flex-col items-center text-gray-100">
                <i class="fas fa-heartbeat text-red-600"></i>
                <p class="text-xs font-bold mt-1">{{ calculatedStats.maxHp }}</p>
              </div>
              <div class="flex flex-col items-center text-gray-100">
                <i class="fas fa-fist-raised text-orange-600"></i>
                <p class="text-xs font-bold mt-1">{{ calculatedStats.atk }}</p>
              </div>
              <div class="flex flex-col items-center text-gray-100">
                <i class="fas fa-running text-green-600"></i>
                <p class="text-xs font-bold mt-1">{{ calculatedStats.evasion }}%</p>
              </div>
              <div class="flex flex-col items-center text-gray-100">
                <i class="fas fa-crosshairs text-yellow-600"></i>
                <p class="text-xs font-bold mt-1">{{ calculatedStats.critical }}%</p>
              </div>
              <div class="flex flex-col items-center text-gray-100">
                <i class="fa-solid fa-shield-virus text-purple-600"></i>
                <p class="text-xs font-bold mt-1">{{ calculatedStats.criticalReduction }}%</p>
              </div>
              <div class="flex flex-col items-center text-gray-100">
                <i class="fas fa-shield-alt text-blue-600"></i>
                <p class="text-xs font-bold mt-1">{{ calculatedStats.defense }}</p>
              </div>
              <div class="flex flex-col items-center text-gray-100">
                <i class="fa-solid fa-person-rays text-indigo-600"></i>
                <p class="text-xs font-bold mt-1">{{ calculatedStats.damageReduction }}%</p>
              </div>
            </div>
          </div>
          <img
            :src="characterData?.imageUrl || 'default-character.png'"
            alt="Character"
            class="w-16 sm:w-20 h-16 sm:h-20 rounded-md object-cover border-2 border-blue-700"
            @error="handleImageError"
          />
        </div>
        <div class="w-full space-y-2">
          <!-- HP Bar -->
          <div class="relative">
            <div class="bg-gray-500 h-5 rounded overflow-hidden">
              <div class="bg-red-600 h-5 rounded transition-all duration-200"
                   :style="{ width: `${(currentHealth / calculatedStats.maxHp * 100) || 0}%` }"></div>
              <p class="absolute inset-0 flex items-center justify-center text-xs text-gray-100">
                HP: {{ currentHealth }} / {{ calculatedStats.maxHp }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <!-- Action Buttons -->
      <div class="flex flex-col gap-2">
        <button
          v-if="ghostData"
          @click="attackGhostHandler()"
          class="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :disabled="isFighting || isAutoAttacking">
          <div v-if="isFighting && !isAutoAttacking" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-skull-crossbones text-xs group-hover:animate-pulse"></i>
          โจมตี
        </button>
        <div v-if="ghostData" class="flex gap-2">
          <button
            @click="toggleAutoAttack(250)"
            class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
            :class="isAutoAttacking && autoAttackIntervalMs === 250 ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'"
            :disabled="!isAutoAttacking && (isFighting || !ghostData)">
            <div v-if="isFighting && isAutoAttacking && autoAttackIntervalMs === 250" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
            <i v-else class="fas fa-robot text-xs group-hover:animate-pulse"></i>
            {{ isAutoAttacking && autoAttackIntervalMs === 250 ? '(0.25s)' : '(0.25s)' }}
          </button>
          <button
            @click="toggleAutoAttack(500)"
            class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
            :class="isAutoAttacking && autoAttackIntervalMs === 500 ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'"
            :disabled="!isAutoAttacking && (isFighting || !ghostData)">
            <div v-if="isFighting && isAutoAttacking && autoAttackIntervalMs === 500" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
            <i v-else class="fas fa-robot text-xs group-hover:animate-pulse"></i>
            {{ isAutoAttacking && autoAttackIntervalMs === 500 ? '(0.5s)' : '(0.5s)' }}
          </button>
          <button
            @click="toggleAutoAttack(1000)"
            class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
            :class="isAutoAttacking && autoAttackIntervalMs === 1000 ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'"
            :disabled="!isAutoAttacking && (isFighting || !ghostData)">
            <div v-if="isFighting && isAutoAttacking && autoAttackIntervalMs === 1000" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
            <i v-else class="fas fa-robot text-xs group-hover:animate-pulse"></i>
            {{ isAutoAttacking && autoAttackIntervalMs === 1000 ? '(1s)' : '(1s)' }}
          </button>
        </div>
        <!-- Fight Result -->
        <p v-if="fightResult" class="text-xs text-gray-300 text-center">{{ fightResult }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { projectFunctions, projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';

export default {
  props: {
    eventId: {
      type: String,
      required: true,
    },
  },
  emits: ['fightCompleted', 'close'],
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();
    const gameActions = projectFunctions.httpsCallable('gameActions');

    // Reactive States
    const isLoading = ref(true);
    const ghostData = ref(null);
    const characterData = computed(() => playerStore.characterData);
    const characterPrivateData = computed(() => playerStore.characterPrivateData);
    const currentGhostHp = ref(0);
    const fightResult = ref('');
    const isFighting = ref(false);
    const expiresAt = ref(null);
    const timeLeft = ref(null);
    const energyLevel = computed(() => playerStore.characterPrivateData?.energyLevel || 0);
    const maxEnergy = ref(4800);
    const isAutoAttacking = ref(false);
    const autoAttackIntervalMs = ref(null);
    const mapName = ref('');
    const isClosed = ref(false);

    // Timers & Unsubscribes
    let timer = null;
    let autoAttackInterval = null;
    let unsubscribeEvent = null;
    let unsubscribeGhost = null;

    // Computed
    const ghostStats = computed(() => {
      if (!ghostData.value) {
        return { maxHp: 100, atk: 0, critical: 0, evasion: 0, criticalReduction: 0, defense: 0, damageReduction: 0 };
      }
      const stats = ghostData.value.stats || {};
      const luck = stats.luck || 0;
      const defense = stats.defense || 0;
      const defenseBonus = Math.floor(defense / 20);
      return {
        maxHp: (stats.stamina || 0) * 20 + (stats.maxHp || 0),
        atk: (stats.str || 0) * 2 + (stats.atk || 0),
        critical: Math.floor(((stats.int || 0) / 2) + (stats.critical || 0)),
        evasion: Math.floor(((stats.agi || 0) / 2) + (stats.evasion || 0)),
        criticalReduction: Math.floor(luck) + defenseBonus,
        defense: defense,
        damageReduction: defenseBonus,
      };
    });

    const calculatedStats = computed(() => {
      if (!characterPrivateData.value) {
        return { maxHp: 100, atk: 0, critical: 0, evasion: 0, criticalReduction: 0, defense: 0, damageReduction: 0 };
      }
      const stats = characterPrivateData.value;
      const str =
        (stats.itemStats?.str || 0) +
        (stats.bonusStats?.str || 0) +
        (stats.stats?.str || 0) +
        (stats.traitStats?.str || 0) +
        (stats.skillStats?.str || 0) +
        (stats.titleStats?.str || 0) +
        (stats.tattooStats?.str || 0) +
        (stats.ghostStats?.str || 0);
      const stamina =
        (stats.itemStats?.stamina || 0) +
        (stats.bonusStats?.stamina || 0) +
        (stats.stats?.stamina || 0) +
        (stats.traitStats?.stamina || 0) +
        (stats.skillStats?.stamina || 0) +
        (stats.titleStats?.stamina || 0) +
        (stats.tattooStats?.stamina || 0) +
        (stats.ghostStats?.stamina || 0);
      const int =
        (stats.itemStats?.int || 0) +
        (stats.bonusStats?.int || 0) +
        (stats.stats?.int || 0) +
        (stats.traitStats?.int || 0) +
        (stats.skillStats?.int || 0) +
        (stats.titleStats?.int || 0) +
        (stats.tattooStats?.int || 0) +
        (stats.ghostStats?.int || 0);
      const agi =
        (stats.itemStats?.agi || 0) +
        (stats.bonusStats?.agi || 0) +
        (stats.stats?.agi || 0) +
        (stats.traitStats?.agi || 0) +
        (stats.skillStats?.agi || 0) +
        (stats.titleStats?.agi || 0) +
        (stats.tattooStats?.agi || 0) +
        (stats.ghostStats?.agi || 0);
      const luck =
        (stats.itemStats?.luck || 0) +
        (stats.bonusStats?.luck || 0) +
        (stats.stats?.luck || 0) +
        (stats.traitStats?.luck || 0) +
        (stats.skillStats?.luck || 0) +
        (stats.titleStats?.luck || 0) +
        (stats.tattooStats?.luck || 0) +
        (stats.ghostStats?.luck || 0);
      const defense =
        (stats.itemStats?.defense || 0) +
        (stats.bonusStats?.defense || 0) +
        (stats.stats?.defense || 0) +
        (stats.traitStats?.defense || 0) +
        (stats.skillStats?.defense || 0) +
        (stats.titleStats?.defense || 0) +
        (stats.tattooStats?.defense || 0) +
        (stats.ghostStats?.defense || 0);
      const flatMaxHp =
        (stats.itemStats?.maxHp || 0) +
        (stats.bonusStats?.maxHp || 0) +
        (stats.stats?.maxHp || 0) +
        (stats.traitStats?.maxHp || 0) +
        (stats.skillStats?.maxHp || 0) +
        (stats.titleStats?.maxHp || 0) +
        (stats.tattooStats?.maxHp || 0) +
        (stats.ghostStats?.maxHp || 0);
      const flatAtk =
        (stats.itemStats?.atk || 0) +
        (stats.bonusStats?.atk || 0) +
        (stats.stats?.atk || 0) +
        (stats.traitStats?.atk || 0) +
        (stats.skillStats?.atk || 0) +
        (stats.titleStats?.atk || 0) +
        (stats.tattooStats?.atk || 0) +
        (stats.ghostStats?.atk || 0);
      const flatCritical =
        (stats.itemStats?.critical || 0) +
        (stats.bonusStats?.critical || 0) +
        (stats.stats?.critical || 0) +
        (stats.traitStats?.critical || 0) +
        (stats.skillStats?.critical || 0) +
        (stats.titleStats?.critical || 0) +
        (stats.tattooStats?.critical || 0) +
        (stats.ghostStats?.critical || 0);
      const flatEvasion =
        (stats.itemStats?.evasion || 0) +
        (stats.bonusStats?.evasion || 0) +
        (stats.stats?.evasion || 0) +
        (stats.traitStats?.evasion || 0) +
        (stats.skillStats?.evasion || 0) +
        (stats.titleStats?.evasion || 0) +
        (stats.tattooStats?.evasion || 0) +
        (stats.ghostStats?.evasion || 0);
      const defenseBonus = Math.floor(defense / 20);
      return {
        maxHp: stamina * 20 + flatMaxHp,
        atk: str * 2 + flatAtk,
        critical: Math.floor((int / 2) + flatCritical),
        evasion: Math.floor((agi / 2) + flatEvasion),
        criticalReduction: Math.floor(luck) + defenseBonus,
        defense: defense,
        damageReduction: defenseBonus,
      };
    });

    const currentHealth = computed(() => {
      const damage = characterPrivateData.value?.damageTaken || 0;
      return Math.max(0, calculatedStats.value.maxHp - damage);
    });

    const isDanger = computed(() => (timeLeft.value ?? 0) <= 300000);

    // Helpers
    const formatTime = (ms) => {
      if (ms <= 0) return 'หมดเวลา!';
      const seconds = Math.floor((ms / 1000) % 60);
      const minutes = Math.floor((ms / (1000 * 60)) % 60);
      return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
    };

    const handleImageError = (event) => {
      event.target.src = 'https://via.placeholder.com/40?text=Image+Not+Found';
    };

    const startCountdown = () => {
      if (expiresAt.value && !timer) {
        timeLeft.value = expiresAt.value - Date.now();
        if (timeLeft.value > 0) {
          timer = setInterval(() => {
            const now = Date.now();
            timeLeft.value = expiresAt.value - now;
            if (timeLeft.value <= 0) {
              clearInterval(timer);
              timer = null;
              if (!fightResult.value) {
                fightResult.value = 'หมดเวลา! ผู้เล่นตายทันที!';
                stopAutoAttack('อีเวนต์หมดเวลา');
              }
            }
          }, 1000);
        } else {
          timeLeft.value = 0;
          if (!fightResult.value) {
            fightResult.value = 'หมดเวลา! ผู้เล่นตายทันที!';
            stopAutoAttack('อีเวนต์หมดเวลา');
          }
        }
      }
    };

    const stopCountdown = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    // Close Handler
    const closeModal = () => {
      isClosed.value = true;
      fightResult.value = 'หน้าต่างถูกปิด';
      stopAutoAttack('หน้าต่างถูกปิด');
      emit('close');
    };

    // Auto Attack Functions
    const toggleAutoAttack = (intervalMs) => {
      if (isAutoAttacking.value) {
        stopAutoAttack('ผู้เล่นปิด Auto Attack');
      } else {
        startAutoAttack(intervalMs);
        fightResult.value = `Auto Attack ถูกเปิด (${intervalMs}ms)`;
      }
    };

    const startAutoAttack = (intervalMs) => {
      if (isAutoAttacking.value) return;
      isAutoAttacking.value = true;
      autoAttackIntervalMs.value = intervalMs;
      if (autoAttackInterval) clearInterval(autoAttackInterval);
      autoAttackInterval = setInterval(async () => {
        if (!ghostData.value) {
          stopAutoAttack('ไม่มีวิญญาณ: Auto Attack หยุด');
          return;
        }
        if (!isFighting.value) {
          try {
            await attackGhostHandler();
          } catch (e) {
            fightResult.value = (e?.message || '').toString();
          }
        }
      }, intervalMs);
    };

    const stopAutoAttack = (reason = '') => {
      if (autoAttackInterval) {
        clearInterval(autoAttackInterval);
        autoAttackInterval = null;
      }
      isAutoAttacking.value = false;
      autoAttackIntervalMs.value = null;
      if (reason) fightResult.value = reason;
    };

    // Cloud Function Actions
    const attackGhostHandler = async () => {
      if (isFighting.value) return;
      isFighting.value = true;
      fightResult.value = 'กำลังโจมตี...';
      try {
        const response = await gameActions({ action: 'attackRoomGuardianGhost', eventId: props.eventId });
        fightResult.value = response.data.message;
        if (response.data.completed || fightResult.value.includes('ผู้เล่นตาย')) {
          stopCountdown();
          stopAutoAttack('การต่อสู้สิ้นสุด');
          emit('fightCompleted');
        }
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการโจมตี';
      } finally {
        isFighting.value = false;
      }
    };

    // Firestore Subscription
    const subscribeToData = async () => {
      try {
        unsubscribeEvent = projectFirestore.collection('events').doc(props.eventId)
          .onSnapshot(async (doc) => {
            if (doc.exists) {
              const data = doc.data();
              mapName.value = data.mapName || '';
              const newExpiresAt = data.expiresAt?.toDate().getTime() || null;
              if (newExpiresAt !== expiresAt.value) {
                expiresAt.value = newExpiresAt;
                stopCountdown();
                if (expiresAt.value) startCountdown();
              } else if (expiresAt.value && !timer) {
                startCountdown();
              }
              if (data.currentGhostId) {
                if (!unsubscribeGhost || ghostData.value?.id !== data.currentGhostId) {
                  if (unsubscribeGhost) unsubscribeGhost();
                  unsubscribeGhost = projectFirestore.collection('ghosts').doc(data.currentGhostId)
                    .onSnapshot((ghostDoc) => {
                      if (ghostDoc.exists) {
                        ghostData.value = { id: ghostDoc.id, ...ghostDoc.data() };
                        currentGhostHp.value = ghostStats.value.maxHp - (ghostDoc.data().damageTaken || 0);
                      } else {
                        ghostData.value = null;
                        currentGhostHp.value = 0;
                      }
                    });
                }
              } else {
                if (unsubscribeGhost) {
                  unsubscribeGhost();
                  unsubscribeGhost = null;
                }
                ghostData.value = null;
                currentGhostHp.value = 0;
              }
            }
            isLoading.value = false;
          });

      } catch (_err) {
        fightResult.value = 'ไม่สามารถโหลดข้อมูลได้';
        isLoading.value = false;
      }
    };

    // Lifecycle
    onMounted(() => {
      subscribeToData();
    });

    onUnmounted(() => {
      stopCountdown();
      stopAutoAttack();
      if (unsubscribeEvent) unsubscribeEvent();
      if (unsubscribeGhost) unsubscribeGhost();
    });

    // Watchers
    watch(currentHealth, (newHealth) => {
      if (newHealth <= 0) {
        stopAutoAttack('ผู้เล่นตาย');
        stopCountdown();
        emit('fightCompleted');
      }
    });

    // Expose to template
    return {
      isClosed,
      closeModal,
      isLoading,
      ghostData,
      ghostStats,
      currentGhostHp,
      characterData,
      characterPrivateData,
      calculatedStats,
      currentHealth,
      energyLevel,
      maxEnergy,
      fightResult,
      isFighting,
      timeLeft,
      mapName,
      isAutoAttacking,
      autoAttackIntervalMs,
      formatTime,
      handleImageError,
      attackGhostHandler,
      toggleAutoAttack,
      isDanger,
    };
  },
};
</script>

<style scoped>
/* Scrollbar */
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}
/* Animations */
@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}
@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}
@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}
.animate-pulse {
  animation: pulse 1.5s infinite;
}
@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
.animate-bounce {
  animation: bounce 0.5s infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
@keyframes spin-pulse {
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.2); }
  100% { transform: rotate(360deg) scale(1); }
}
.animate-spin-pulse {
  animation: spin-pulse 1.2s ease-in-out infinite;
}
/* Button States */
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
/* Responsive */
@media (max-width: 640px) {
  .text-sm {
    font-size: 0.75rem;
  }
  .text-xs {
    font-size: 0.625rem;
  }
  .p-4 {
    padding: 0.75rem;
  }
  .w-16 {
    width: 3rem;
    height: 3rem;
  }
}
/* Extra tiny text utility */
.text-xxs { font-size: 10px; line-height: 1rem; }
</style>
